import { computed, type ComputedRef, type Ref } from "vue";
import type { EventType } from "@/Pages/Zaptime/App/EventTypes/types/EventType";
import type { EventTypeUser } from "@/Pages/Zaptime/App/EventTypes/types/EventTypeUser";

export type WarningType =
    | "no_availability"
    | "no_calendar_destination"
    | "no_location"
    | "no_team_users";

export type Warning = {
    type: WarningType;
    tab: string;
    messageKey: string;
};

type Integration = {
    id: number;
    type: string;
    [key: string]: unknown;
};

export function useEventTypeWarnings(
    eventType: Ref<EventType> | ComputedRef<EventType> | EventType,
    eventTypeUser?:
        | Ref<EventTypeUser | undefined>
        | ComputedRef<EventTypeUser | undefined>
        | EventTypeUser
        | undefined,
    integrations?:
        | Ref<Integration[] | undefined>
        | ComputedRef<Integration[] | undefined>
        | Integration[]
        | undefined,
) {
    const warnings = computed<Warning[]>(() => {
        const result: Warning[] = [];
        const et =
            eventType && "value" in eventType ? eventType.value : eventType;
        const etu = eventTypeUser
            ? "value" in eventTypeUser
                ? eventTypeUser.value
                : eventTypeUser
            : undefined;
        const ints = integrations
            ? "value" in integrations
                ? integrations.value
                : integrations
            : undefined;

        if (!et) {
            return result;
        }

        // Check if user has any calendar integrations available (Google or iCloud)
        const calendarIntegrationTypes = ["GOOGLE", "ICLOUD_CALENDAR"];
        const hasCalendarIntegrationsAvailable =
            ints &&
            ints.some((integration) =>
                calendarIntegrationTypes.includes(integration.type),
            );

        // Warning 1: No availability source configured
        // - use_availability is true but availability_id is not set
        // - OR neither use_availability nor use_calendar_integration_availability is enabled
        if (etu) {
            const noAvailabilitySelected =
                etu.use_availability && !etu.availability_id;
            const noAvailabilitySourceEnabled =
                !etu.use_availability &&
                !etu.use_calendar_integration_availability;

            if (noAvailabilitySelected || noAvailabilitySourceEnabled) {
                result.push({
                    type: "no_availability",
                    tab: "availability",
                    messageKey: "event_types.warnings.no_availability",
                });
            }
        }

        // Warning 2: User has calendar integrations but no destination calendar selected
        if (hasCalendarIntegrationsAvailable && etu) {
            const integrationData = etu.integration_data;
            // integration_data can be an empty array [] or an object with calendars
            const hasDestinationCalendar =
                integrationData &&
                typeof integrationData === "object" &&
                !Array.isArray(integrationData) &&
                integrationData.calendars &&
                integrationData.calendars.length > 0;

            if (!hasDestinationCalendar) {
                result.push({
                    type: "no_calendar_destination",
                    tab: "calendars-and-hosts",
                    messageKey: "event_types.warnings.no_calendar_destination",
                });
            }
        }

        // Warning 3: No location configured
        if (!et.locations || et.locations.length === 0) {
            result.push({
                type: "no_location",
                tab: "booking-page",
                messageKey: "event_types.warnings.no_location",
            });
        }

        // Warning 4: Team event type without users assigned
        if (et.isTeamEventType && (!et.users || et.users.length === 0)) {
            result.push({
                type: "no_team_users",
                tab: "team",
                messageKey: "event_types.warnings.no_team_users",
            });
        }

        return result;
    });

    const hasWarnings = computed(() => warnings.value.length > 0);

    function tabHasWarning(tabName: string): ComputedRef<boolean> {
        return computed(() => warnings.value.some((w) => w.tab === tabName));
    }

    function warningsForTab(tabName: string): ComputedRef<Warning[]> {
        return computed(() => warnings.value.filter((w) => w.tab === tabName));
    }

    return {
        warnings,
        hasWarnings,
        tabHasWarning,
        warningsForTab,
    };
}
